import { createGlobalState, useAsyncState, useSessionStorage, useWindowSize, whenever } from '@vueuse/core';
import { useRouteQuery } from '@vueuse/router';
import { isAxiosError } from 'axios';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useJobData } from '@/composables/job-data';
import router from '@/router';
import { chatHistoryRouteChatHistoryGet, ChatMessage } from '@/services/python-api';
import { identify } from '@/services/tracking';
import { AssistantChatMessage, LocalChatMessage, UserChatMessage } from '@/types/chat';

let historyAbortController: AbortController | null = null;

// Blacklisted session IDs that cannot be reused
const BLACKLISTED_SESSION_IDS = new Set<string>(['session-1760623151065']);

export const useSessionStore = createGlobalState(() => {
  const { clearJobData } = useJobData();
  const urlParams = new URLSearchParams(window.location.search);
  const urlSessionId = urlParams.get('session');
  const sessionId = useRouteQuery<string>('session', urlSessionId!);

  const { width, height } = useWindowSize();
  const { locale: language } = useI18n();

  whenever(
    sessionId,
    () => {
      identify({
        sessionId: sessionId.value,
        width: width.value,
        height: height.value,
        language: language.value,
      });
    },
    { immediate: true },
  );

  const historyIsFirstLoading = ref<boolean>();
  const userMessagesLimit = 50;
  // true by default so dialogs can be opened right away when conditions are met
  const userMessagesLimitModalOpen = useSessionStorage('userMessagesLimitModalOpen', true);
  const userMessagesPrelimitModalOpen = useSessionStorage('userMessagesPrelimitModalOpen', true);

  // Prevent replacing route on first load
  router.isReady().then(() => {
    if (BLACKLISTED_SESSION_IDS.has(sessionId.value)) {
      newSession();
    }
  });

  const messages = ref<LocalChatMessage[]>([]);

  const userMessagesLimitReached = computed(() => messages.value.length >= userMessagesLimit);

  const initMessages = (newHistory: ChatMessage[]) => {
    hasSentFirstMessage.value = newHistory.length > 0;

    messages.value = newHistory.map((msg) => {
      if (msg.type === 'user') {
        return {
          type: 'user',
          content: msg.content,
          timestamp: msg.timestamp,
        } satisfies UserChatMessage;
      } else {
        return {
          type: 'assistant',
          content: msg.content,
          timestamp: msg.timestamp,
          isTyping: false,
          thinkingBlocks: [],
        } satisfies AssistantChatMessage;
      }
    });
  };

  const { execute: fetchHistory, isLoading: isFetchingHistory } = useAsyncState(
    async () => {
      if (historyAbortController) {
        historyAbortController.abort();
      }

      historyAbortController = new AbortController();

      if (historyIsFirstLoading.value === undefined) {
        historyIsFirstLoading.value = true;
      }

      try {
        const { data } = await chatHistoryRouteChatHistoryGet({
          throwOnError: true,
          query: { session_id: sessionId.value },
          signal: historyAbortController.signal,
        });

        return data;
      } catch (error) {
        if (isAxiosError(error) && error.code === 'ERR_CANCELED') {
          return;
        }

        // Prevent app fail if history is not found (eg. in CGU page)
        console.error(error);
        return [];
      } finally {
        historyAbortController = null;
      }
    },
    [],
    {
      immediate: false,
      onSuccess: (history) => {
        if (!history) {
          return;
        }

        initMessages(history);
        historyIsFirstLoading.value = false;
      },
    },
  );

  const hasSentFirstMessage = ref(messages.value.length > 0);
  const isNewDiscussion = computed(() => !hasSentFirstMessage.value);

  const newSession = () => {
    clearJobData();
    sessionId.value = `session-${Date.now()}`;
    hasSentFirstMessage.value = false;
    messages.value = [];
    userMessagesLimitModalOpen.value = true;
    userMessagesPrelimitModalOpen.value = true;
  };

  const userMessages = computed(() => messages.value.filter((message) => message.type === 'user'));

  return {
    sessionId: computed(() => sessionId.value),
    newSession,
    messages,
    isNewDiscussion,
    hasSentFirstMessage,
    fetchHistory,
    isFetchingHistory,
    userMessages,
    historyIsFirstLoading,
    userMessagesLimit,
    userMessagesLimitModalOpen,
    userMessagesPrelimitModalOpen,
    userMessagesLimitReached,
  };
});
